import React, { useEffect, useState, useCallback } from "react";
import DetailBox from "./DetailBox";
import SearchBar from "../layout/SearchBar";
import SimSwapTable from "./SimSwapTable";
import Pagination from "../layout/Pagination";
import { Calendar } from "lucide-react";
import { getSimSwapData } from "../../actions/SimSwapAction";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { SIMSWAP_STATUSES } from "../../constants/Common";
import DropdownBox from "../layout/DropdownBox";
import Loading from "../layout/Loading";
import { ReactComponent as GroupIcon } from '../../assets/admin/Group.svg';
import { ReactComponent as AdjustmentsIcon } from '../../assets/admin/Adjustments.svg';
import { formatDate, formatDateOnly } from "../../utils/DateFormat";

// Local getStatusClass to avoid import issues
function getStatusClass(status) {
  const baseClass = "px-5 py-1 rounded-xl text-xs font-medium text-center w-24";
  if (["OPEN"].includes(status)) {
    return `bg-[#DBEAFE] text-[#1E40AF] ${baseClass}`;
  } else if (status === "IN_REVIEW") {
    return `bg-yellow-100 text-[#92400E] ${baseClass}`;
  } else if (["APPROVED"].includes(status)) {
    return `bg-[#D1FAE5] text-[#065F46] ${baseClass}`;
  } else if (status === "REJECTED") {
    return `bg-[#FEE2E2] text-[#991B1B] ${baseClass}`;
  } else {
    return `text-gray-800 ${baseClass}`;
  }
}

const TABLE_HEADERS = [
  { key: "uid", label: "ID" },
  { key: "oldMobileNumber", label: "Customer old number" },
  { key: "customerFirstName", label: "First name" },
  { key: "customerLastName", label: "Last name" },
  { key: "dateOfBirth", label: "Date of Birth" },
  { key: "pukCode", label: "PUK Code " },
  { key: "newSimSerialNumber", label: "New Sim Serial Number" },
  { key: "createdAt", label: "Date & Time" },
  { key: "status", label: "Status", isStatus: true },
  { key: "action", label: "Action", isAction: true },
];

const SimSwapOverview = ({
  searchTerm,
  setSearchTerm,
  filters,
  setFilters,
}) => {
  const [simSwapData, setSimSwapData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm || "");
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [selectedSimSwapData, setSelectedSimSwapData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Group popup state and logic
  const [showColumnPopup, setShowColumnPopup] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(TABLE_HEADERS.map(h => h.key));

  const handleColumnToggle = (key) => {
    setSelectedColumns((prev) =>
      prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key]
    );
  };

  const handleResetColumns = () => {
    setSelectedColumns(TABLE_HEADERS.map(h => h.key));
  };

  const handleApplyColumns = () => {
    setShowColumnPopup(false);
  };

  const columnPopup = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={() => setShowColumnPopup(false)}>
      <div className="bg-white rounded-lg p-6 w-80 max-w-sm shadow-lg" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-4">Edit columns</h2>
        <div className="flex flex-col mb-4">
          {TABLE_HEADERS.map((header, idx) => (
            <React.Fragment key={header.key}>
              <label className="flex items-center gap-2 text-base py-2">
                <input
                  type="checkbox"
                  checked={selectedColumns.includes(header.key)}
                  onChange={() => handleColumnToggle(header.key)}
                  className="accent-primary w-4 h-4"
                />
                <span>{header.label} <span className="text-xs text-gray-400"></span></span>
              </label>
              {idx < TABLE_HEADERS.length - 1 && (
                <hr className="border-t border-gray-200" />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <button
            onClick={handleResetColumns}
            className="border border-gray-300 rounded-md px-4 py-2 text-sm font-medium bg-white text-gray-700"
          >
            Reset
          </button>
          <button
            onClick={handleApplyColumns}
            style={{ backgroundColor: '#22C55E' }}
            className="text-white rounded-md px-4 py-2 text-sm font-medium"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );

  // Single optimized function for fetching data
  const fetchSimSwapData = useCallback(
    async (searchTermParam, filtersParam, pageParam) => {
      setIsLoading(true);
      const pagination = {
        page: pageParam,
        limit: itemsPerPage,
      };

      try {
        const response = await getSimSwapData(
          pagination,
          searchTermParam.trim(),
          filtersParam
        );

        if (response.success) {
          setSimSwapData(response.data);
          setCurrentPage(response.currentPage);
          setTotalPages(response.totalPages);
          setTotalCount(response.totalCount);
        } else {
          toast.error(response.message || "Error: Unable to fetch data.");
          setSimSwapData([]);
          setTotalPages(1);
          setTotalCount(0);
          setCurrentPage(1);
        }
      } catch (error) {
        toast.error("Something went wrong. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [itemsPerPage]
  );

  // Function for normal data fetching (Apply button)
  const getAllSimSwapData = useCallback(() => {
    fetchSimSwapData(searchTerm, filters, currentPage);
  }, [fetchSimSwapData, searchTerm, filters, currentPage]);

  useEffect(() => {
    getAllSimSwapData();
  }, [currentPage, itemsPerPage, searchTerm.trim()]);

  const handleSearch = () => {
    setSearchTerm(localSearchTerm);
  };

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleFilterChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFilters((prevFilters) => ({
        ...prevFilters,
        [name]: value,
      }));
    },
    [setFilters]
  );

  const resetFilters = () => {
    const emptyFilters = {
      status: "",
      fromDate: "",
      toDate: "",
    };
    setLocalSearchTerm("");
    setSearchTerm("");
    setFilters(emptyFilters);
    setCurrentPage(1);
    fetchSimSwapData("", emptyFilters, 1);
  };

  // Mobile filter popup
  const mobileFilterPopup = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={() => setShowMobileFilter(false)}>
      <div className="bg-white rounded-lg p-4 w-11/12 max-w-sm" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-4">Filter Sim Swaps</h2>
        <div className="mb-3">
           <label className="primary_label">Select status</label>
          <DropdownBox
            optionList={[{ _id: "", name: "ALL" }, ...SIMSWAP_STATUSES.map(status => ({ _id: status, name: status }))]}
            styleName="w-full text-sm font-normal text-text1 focus:outline-none"
            value={filters.status || ""}
            placeholder="Select status"
            displayKey="name"
            valueKey="_id"
            isObjectData={true}
            handleChange={selectedValue => handleFilterChange({ target: { name: "status", value: selectedValue } })}
            emptyStringName="ALL"
            searchable={true} 
          />
        </div>
        <div className="mb-3">
          <label className="primary_label">From Date</label>
          <input
            type="date"
            className="primary_input"
            value={filters.fromDate || ""}
            onChange={handleFilterChange}
            max={filters.toDate}
            name="fromDate"
          />
        </div>
        <div className="mb-3">
          <label className="primary_label">To Date</label>
          <input
            type="date"
            className="primary_input"
            value={filters.toDate || ""}
            onChange={handleFilterChange}
            min={filters.fromDate}
            name="toDate"
          />
        </div>
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => { resetFilters(); setShowMobileFilter(false); }}
            className="secondary_button inactive flex-1 text-[12px] sm:text-sm"
          >
            Reset
          </button>
          <button
            onClick={() => { getAllSimSwapData(); setShowMobileFilter(false); }}
            className="secondary_button active flex-1 text-[12px] sm:text-sm"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white px-4 sm:px-10">
      {/* Mobile view */}
      <div className="block md:hidden">
        
        <div className="flex flex-row items-center w-full gap-2 pt-4">
          <div className="flex-1 flex items-center h-9">
            <SearchBar
              searchTerm={localSearchTerm}
              onChange={value => setLocalSearchTerm(value)}
              onSearch={handleSearch}
              className="w-full"
            />
          </div>
          <div className="flex items-center">
            <button
              className="flex items-center justify-center h-9 p-0"
              aria-label="Group"
              style={{ minWidth: 40, borderRadius: '6px', border: '1px solid #E5E7EB', background: '#FFF', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)' }}
              onClick={() => setShowColumnPopup(true)}
            >
              <GroupIcon className="w-6 h-6 text-gray-500" />
            </button>
          </div>
          <div className="flex items-center">
            <button
              className="flex items-center justify-center h-9 p-0"
              aria-label="Filter"
              style={{ minWidth: 40, borderRadius: '6px', border: '1px solid #E5E7EB', background: '#FFF', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)' }}
              onClick={() => setShowMobileFilter(true)}
            >
              <AdjustmentsIcon className="w-6 h-6 text-gray-500" />
            </button>
          </div>
        </div>
          <div className="mt-6">
            <div className="rounded-lg border-t border-x border-b border-gray-300 overflow-x-auto">
              <div className="mt-0 flex flex-col gap-0">
                {isLoading ? (
                  <Loading />
                ) : (
                  simSwapData && simSwapData.length > 0 ? (
                    simSwapData.map((simSwap) => (
                      <table key={simSwap._id || simSwap.uid} className="w-full border border-gray-200 rounded-none bg-white shadow-sm">
                        <tbody>
                          {TABLE_HEADERS.filter(header => selectedColumns.includes(header.key)).map((header) => (
                            <tr key={header.key}>
                                <td className="py-2 px-2 text-[16px] font-medium font-rubik text-gray-900 w-1/2 align-top">{header.label}</td>
                              <td className="py-2 px-2 text-[16px] font-normal font-rubik text-gray-700 w-1/2 align-top overflow-hidden text-ellipsis">

                              {header.key === "createdAt" ? (
                              simSwap[header.key] ? (
                                formatDate(simSwap[header.key])
                              ) : (
                                "N/A"
                              )
                            ) : header.key === "dateOfBirth" ? (
                              simSwap[header.key] ? (
                                formatDateOnly(simSwap[header.key])
                              ) : (
                                "N/A"
                              )
                            ) : header.isAction ? (
                              <button
                                className="flex items-center justify-center p-2 bg-gray-100 rounded-md"
                                onClick={() => {
                                  setSelectedSimSwapData(simSwap);
                                  setIsModalOpen(true);
                                }}
                              >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                  </button>
                                ) : header.isStatus ? (
                                  <span className={getStatusClass(simSwap.status)}>
                                    {simSwap.status ? simSwap.status.charAt(0).toUpperCase() + simSwap.status.slice(1).toLowerCase() : ""}
                                  </span>
                                ) : header.key.includes(".") ? (
                                  header.key.split(".").reduce((o, key) => (o ? o[key] : ""), simSwap) || "N/A"
                                ) : (
                                  simSwap[header.key] || "N/A"
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 p-4 bg-white border border-gray-200 rounded-lg">No sim swaps found</div>
                  )
                )}
              </div>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        {isModalOpen && selectedSimSwapData && (
          <DetailBox
            closeModal={() => { setIsModalOpen(false); setSelectedSimSwapData(null); }}
            selectedSimSwap={selectedSimSwapData}
            getStatusClass={status => {
              const baseClass = "px-5 py-1 rounded-xl text-xs font-medium text-center w-24";
              if (status === "OPEN") return `bg-[#DBEAFE] text-[#1E40AF] ${baseClass}`;
              if (status === "IN_REVIEW") return `bg-yellow-100 text-[#92400E] ${baseClass}`;
              if (status === "APPROVED") return `bg-[#D1FAE5] text-[#065F46] ${baseClass}`;
              if (status === "REJECTED") return `bg-[#FEE2E2] text-[#991B1B] ${baseClass}`;
              return `text-gray-800 ${baseClass}`;
            }}
            formatStatus={status => status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : ""}
            SIMSWAP_STATUSES={SIMSWAP_STATUSES}
          />
        )}
        {showColumnPopup && columnPopup}
        {/*  */}
        {showMobileFilter && mobileFilterPopup}
        {isModalOpen && selectedSimSwapData && (
          <DetailBox
            closeModal={() => { setIsModalOpen(false); setSelectedSimSwapData(null); }}
            selectedSimSwap={selectedSimSwapData}
            getStatusClass={status => {
              const baseClass = "px-5 py-1 rounded-xl text-xs font-medium text-center w-24";
              if (status === "OPEN") return `bg-[#DBEAFE] text-[#1E40AF] ${baseClass}`;
              if (status === "IN_REVIEW") return `bg-yellow-100 text-[#92400E] ${baseClass}`;
              if (status === "APPROVED") return `bg-[#D1FAE5] text-[#065F46] ${baseClass}`;
              if (status === "REJECTED") return `bg-[#FEE2E2] text-[#991B1B] ${baseClass}`;
              return `text-gray-800 ${baseClass}`;
            }}
            formatStatus={status => status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : ""}
            SIMSWAP_STATUSES={SIMSWAP_STATUSES}
          />
        )}
      </div>
      

      {/* Desktop view */}
      <div className="hidden md:block">
        <div>
          {/* filters */}
          <div className="flex w-full gap-4">
            {/* Status Dropdown */}
            <div className="flex-1">
              <label className="primary_label">Status</label>
              <DropdownBox
                optionList={[
                  { _id: "", name: "ALL" },
                  ...SIMSWAP_STATUSES.map((status) => ({
                    _id: status,
                    name: status,
                  })),
                ]}
                styleName="rounded px-1 text-sm font-normal text-text1 uppercase focus:outline-none focus:ring-0 w-full"
                value={filters.status || ""}
                placeholder="Select Status"
                displayKey="name"
                valueKey="_id"
                isObjectData={true}
                handleChange={(selectedValue) =>
                  handleFilterChange({
                    target: {
                      name: "status",
                      value: selectedValue,
                    },
                  })
                }
                emptyStringName="ALL"
                searchable={true} 
              />
            </div>
            {/* From Date */}
            <div className="flex-1">
              <label htmlFor="fromDate" className="primary_label">From Date</label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 px-3 bg-gray-50 border border-gray-300 rounded-tl-md rounded-bl-md flex items-center">
                  <Calendar className="text-md text-gray-500 h-5 w-5" />
                </div>
                <div className="pl-10">
                  <input
                    type="date"
                    id="fromDate"
                    name="fromDate"
                    className="w-full text-text1 py-[10px] px-5 border rounded-md border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-300 appearance-none date-input-no-indicator"
                    value={filters.fromDate || ""}
                    onChange={handleFilterChange}
                    max={filters.toDate}
                  />
                </div>
              </div>
            </div>
            {/* To Date */}
            <div className="flex-1">
              <label htmlFor="toDate" className="primary_label">To Date</label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 px-3 bg-gray-50 border border-gray-300 rounded-tl-md rounded-bl-md flex items-center">
                  <Calendar className="text-md text-gray-500 h-5 w-5" />
                </div>
                <div className="pl-10">
                  <input
                    type="date"
                    id="toDate"
                    name="toDate"
                    className="w-full text-text1 py-[10px] px-5 border rounded-md border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-300 appearance-none date-input-no-indicator"
                    value={filters.toDate}
                    onChange={handleFilterChange}
                    min={filters.fromDate}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* buttons  */}
          <div className="mt-4 gap-3 flex justify-end items-center">
            <button
              onClick={resetFilters}
              className="secondary_button inactive"
              disabled={isLoading}
            >
              Reset
            </button>
            <button
              onClick={getAllSimSwapData}
              className="secondary_button active"
              disabled={isLoading}
            >
              {isLoading ? "Applying..." : "Apply"}
            </button>
          </div>
        </div>
        <hr className="mb-10 border-gray-300 mt-4" />
        {isLoading ? (
          <Loading />
        ) : (
          <div className="space-y-6">
            <div className="flex justify-end items-center">
              <SearchBar
                searchTerm={localSearchTerm}
                onChange={value => setLocalSearchTerm(value)}
                onSearch={handleSearch}
                className="w-96"
              />
            </div>
            <SimSwapTable simSwapData={simSwapData} SIMSWAP_STATUSES={SIMSWAP_STATUSES} />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SimSwapOverview;
